import { ComponentStore } from '@ngrx/component-store';
import { computed, inject, Injectable } from '@angular/core';
import { catchError, concatMap } from 'rxjs/operators';
import { ICorrelation } from '@app/api/interfaces/correlation.interface';
import { EMPTY, Observable, of } from 'rxjs';
import equal from 'fast-deep-equal';
import { IGcComponentStore } from '@app/core/interfaces/gc-component-store.interface';
import { FLOATING_POPUP_DATA } from '@app/main/main-content/floating-popup-manager/providers/floating-popup-data.token';
import { Store } from '@ngrx/store';
import { selectMainProjectId } from '@app/main/main-screen/store/main-selection/main-shared-selection/main-shared-selection.selectors';
import { selectProjectEntities } from '@app/main/main-screen/store/projects/projects.selectors';
import {
  IFpCorrelationGraphState,
  initialFpCorrelationGraphState,
} from '@app/main/leak-detection/floating-popups/floating-popup-correlation/tabs/fp-correlation-graph/services/fp-correlation-graph/fp-correlation-graph.state';
import {
  FpCorrelationGraphActionGetData,
  FpCorrelationGraphActionGetDataError,
  FpCorrelationGraphActionGetDataSuccesses,
  FpCorrelationGraphActions,
  FpCorrelationGraphActionToggleChartFullscreen,
  FpCorrelationGraphActionType,
} from '@app/main/leak-detection/floating-popups/floating-popup-correlation/tabs/fp-correlation-graph/services/fp-correlation-graph/fp-correlation-graph.actions';
import { CorrelationsChartDataApiService } from '@app/api/services/correlation-chart-data-api.service';
import { MeasurementSystem } from '@app/core/enums/measurement-system.enum';
import { FloatingPopupService } from '@app/main/main-content/floating-popup-manager/services/floating-popup.service';
import { convertChartDataInArray } from '@app/shared/utils/base-chart-data';
import {
  ICorrelationCurveChartCommonData,
  ICorrelationCurveChartDataPoint,
} from '@app/api/interfaces/chart-data/correlation-curve';

@Injectable()
export class FpCorrelationGraphStore
  extends ComponentStore<IFpCorrelationGraphState>
  implements IGcComponentStore<FpCorrelationGraphActions>
{
  public readonly data = inject<ICorrelation>(FLOATING_POPUP_DATA);
  protected readonly store = inject(Store);
  protected readonly correlationsChartDataApiService = inject(CorrelationsChartDataApiService);
  public readonly popupService: FloatingPopupService = inject(FloatingPopupService);

  constructor() {
    super(initialFpCorrelationGraphState);
  }

  public readonly date$ = this.select(s => s.date, { equal });
  public readonly popupMainData$ = this.select(s => s.popupMainData, { equal });
  public readonly data$ = this.select(s => s.data, { equal });
  public readonly error$ = this.select(s => s.error, { equal });

  private mainProjectId = this.store.selectSignal(selectMainProjectId);
  private projectEntities = this.store.selectSignal(selectProjectEntities);

  public viewMeasurementSystem = computed(() => {
    const mainProjectId = this.mainProjectId();
    const projectEntities = this.projectEntities();

    const currentProject = mainProjectId ? projectEntities[mainProjectId] : null;

    return currentProject?.viewMeasurementSystem || MeasurementSystem.Metric;
  });

  public readonly loading$ = this.select(s => s.loading, { equal });

  public readonly isChartFullscreenActive$ = this.select(s => s.isChartFullscreenActive, { equal });

  public dispatch = this.effect((action$: Observable<FpCorrelationGraphActions>) =>
    action$.pipe(
      concatMap(action => {
        switch (action.type) {
          case FpCorrelationGraphActionType.GetData: {
            this.reducerGetData();
            this.effectGetData(action);
            break;
          }
          case FpCorrelationGraphActionType.GetDataSuccesses: {
            this.reducerGetDataSuccesses(action);
            break;
          }
          case FpCorrelationGraphActionType.GetDataError: {
            this.reducerGetDataError(action);
            break;
          }
          case FpCorrelationGraphActionType.ToggleChartFullscreen: {
            this.reducerToggleChartFullscreen();
            this.effectToggleChartFullscreen(action);
            break;
          }
        }
        return of(action);
      })
    )
  );

  private reducerGetData() {
    this.patchState({
      loading: true,
      error: null,
    });
  }

  private reducerGetDataSuccesses(action: FpCorrelationGraphActionGetDataSuccesses) {
    this.patchState({
      date: action.date,
      popupMainData: action.popupMainData,
      data: action.data,
      loading: false,
    });
  }

  private reducerGetDataError(action: FpCorrelationGraphActionGetDataError) {
    this.patchState({
      date: action.date,
      popupMainData: action.popupMainData,
      data: null,
      error: action.error,
      loading: false,
    });
  }

  private reducerToggleChartFullscreen() {
    const state = this.get();
    this.patchState({
      isChartFullscreenActive: !state.isChartFullscreenActive,
    });
  }

  private effectGetData(action: FpCorrelationGraphActionGetData) {
    const { correlation, correlationMode } = action.popupMainData || {};
    const mainProjectId = this.mainProjectId();

    if (!mainProjectId || !correlation) {
      this.dispatch({
        type: FpCorrelationGraphActionType.GetDataSuccesses,
        date: action.date,
        popupMainData: action.popupMainData,
        data: null,
      });

      return;
    }

    const state = this.get();

    const isOldDataValid =
      state.data &&
      state.popupMainData &&
      state.popupMainData.correlation.id === correlation.id &&
      state.popupMainData.correlationMode === correlationMode;

    if (isOldDataValid) {
      this.dispatch({
        type: FpCorrelationGraphActionType.GetDataSuccesses,
        date: action.date,
        popupMainData: action.popupMainData,
        data: state.data,
      });

      return;
    }

    this.correlationsChartDataApiService
      .getCurve(mainProjectId, correlation.id, correlationMode)
      .pipe(
        catchError(error => {
          this.dispatch({
            type: FpCorrelationGraphActionType.GetDataError,
            date: action.date,
            popupMainData: action.popupMainData,
            error,
          });

          return EMPTY;
        })
      )
      .subscribe(({ data }) => {
        this.dispatch({
          type: FpCorrelationGraphActionType.GetDataSuccesses,
          date: action.date,
          popupMainData: action.popupMainData,
          data: convertChartDataInArray<ICorrelationCurveChartDataPoint, ICorrelationCurveChartCommonData>(data),
        });
      });
  }

  private effectToggleChartFullscreen(action: FpCorrelationGraphActionToggleChartFullscreen) {
    if (!action.synchronizeWithPopupId) return;

    const state = this.get();

    if (state.isChartFullscreenActive) {
      this.popupService.enterFullscreen(action.synchronizeWithPopupId, false);
    } else {
      this.popupService.exitFullscreen(action.synchronizeWithPopupId, false);
    }
  }
}
